import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as CrossIcon } from "../../shared/assets/cross.svg";

export interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | string;
  customWidth?: string; //
  closeOnBackdropClick?: boolean;
  blurBackdrop?: boolean;
  maxHeight?: string;
  minHeight?: string;
  overflowY?: "auto" | "visible" | "hidden";
  hideScrollbar?: boolean;
  customScrollbar?: boolean;
  footer?: React.ReactNode;
  /**
   * Replaces the body's default padding (`px-3 py-4 sm:px-6 sm:py-6`), for a
   * modal whose content needs to sit closer to the header.
   */
  bodyClassName?: string;
  /**
   * Below 640px the modal takes the whole screen, square, with its footer at
   * the bottom edge: a long form in a floating box left a strip of page around
   * it and the buttons below the keyboard (CTX-1433).
   */
  fullScreenOnPhone?: boolean;
}

const GenericModal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "lg",
  customWidth,
  closeOnBackdropClick = true,
  blurBackdrop = false,
  maxHeight,
  minHeight,
  // overflowY = "visible",
  overflowY = "auto",
  hideScrollbar = false,
  customScrollbar = true,
  footer,
  bodyClassName = "px-3 py-4 sm:px-6 sm:py-6",
  fullScreenOnPhone = false,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  let modalWidthClass = "w-full max-w-md";
  // If customWidth is provided, use it (can be Tailwind class or CSS value)
  if (customWidth) {
    // Check if it's a CSS value (contains px, %, rem, em, etc.)
    if (
      customWidth.includes("px") ||
      customWidth.includes("%") ||
      customWidth.includes("rem") ||
      customWidth.includes("em") ||
      customWidth.includes("vw")
    ) {
      modalWidthClass = "w-full";
    } else {
      // Assume it's a Tailwind class
      modalWidthClass = `w-full ${customWidth}`;
    }
  } else if (size === "sm") {
    modalWidthClass = "w-full max-w-sm";
  } else if (size === "md") {
    modalWidthClass = "w-full max-w-md";
  } else if (size === "lg") {
    modalWidthClass = "w-full max-w-3xl";
  } else {
    modalWidthClass = `w-full ${size}`;
  }

  // Default max-height to 90vh if not provided
  const defaultMaxHeight = maxHeight || "90vh";
  const defaultMinHeight = minHeight;

  // Determine body scrollbar classes
  const getBodyScrollbarClass = () => {
    if (hideScrollbar) {
      return "scrollbar-hide";
    }
    if (customScrollbar) {
      return "custom-scrollbar";
    }
    return "";
  };

  // Determine body overflow behavior
  const getBodyOverflow = () => {
    switch (overflowY) {
      case "hidden":
        return "overflow-hidden";
      case "visible":
        // 'visible' is not compatible with this layout; fallback to auto
        return "overflow-y-auto";
      default:
        return "overflow-y-auto";
    }
  };

  // Render through a portal to <body>. Otherwise the modal mounts inside the
  // page content (`.main-container-global`, which is `position: relative` +
  // `overflow-x: auto`), and its `fixed inset-0` backdrop fails to cover the
  // full viewport — leaving the top SideNav bar (rendered outside that
  // container) undimmed. Portaling escapes that container so the overlay
  // spans the whole screen. Safe for theming: `.theme-dark` is set on
  // <html>, so styles still resolve inside <body>.
  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center ${fullScreenOnPhone ? "p-0" : "px-2 py-4"} sm:px-4 sm:py-6 ml-0!important`}
      aria-modal="true"
      role="dialog"
    >
      <div
        role="button"
        aria-label="Close modal backdrop"
        onClick={() => {
          if (closeOnBackdropClick) onClose();
        }}
        className={
          // base backdrop
          "absolute inset-0 bg-black bg-opacity-30" +
          // conditional blur
          (blurBackdrop ? " backdrop-blur-sm" : "")
        }
      />
      {/* Modal wrapper - never scrolls, uses overflow-hidden */}
      <div
        className={`
                    relative z-10
                    bg-[var(--color-semantic-bg-primary)]
                    rounded-sm
                    border
                    border-[var(--color-semantic-border-secondary)]
                    shadow-lg
                    ${minHeight ? "p-0" : "p-2 sm:p-4"}
                    ${modalWidthClass}
                    flex flex-col
                    overflow-hidden
                    ${fullScreenOnPhone ? "max-sm:h-dvh max-sm:!max-h-none max-sm:max-w-none max-sm:rounded-none max-sm:border-0" : ""}
                `}
        style={{
          maxHeight: defaultMaxHeight,
          minHeight: defaultMinHeight,
          ...(customWidth &&
          (customWidth.includes("px") ||
            customWidth.includes("%") ||
            customWidth.includes("rem") ||
            customWidth.includes("em") ||
            customWidth.includes("vw"))
            ? { maxWidth: customWidth }
            : {}),
        }}
      >
        {/* Header - fixed, flex-shrink-0 */}
        <div className="flex-shrink-0 px-3 sm:px-6 pt-6 pb-4 md:pt-2 border-b border-[var(--color-semantic-border-primary)]">
          {title && (
            <h2 className="text-base font-semibold text-[var(--color-semantic-text-primary)] pr-8 sm:text-lg md:pr-0">
              {title}
            </h2>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="
                            absolute
                            top-6
                            right-4 sm:right-6
                            text-[var(--color-semantic-text-primary)]
                            hover:text-[var(--color-semantic-text-primary)]
                            focus:outline-none
                            flex items-center justify-center
                            w-6 h-6
                            "
          >
            <CrossIcon className="w-32 h-32" />
          </button>
        </div>

        {/* Body - scrollable, flex-1, always uses custom-scrollbar when enabled */}
        <div
          className={`
                        flex-1 min-h-0
                        ${bodyClassName}
                        ${getBodyOverflow()}
                        ${getBodyScrollbarClass()}
                        semantic text-primary
                        text-[var(--color-semantic-text-primary)]
                    `}
        >
          {children}
        </div>

        {/* Footer - fixed, flex-shrink-0 */}
        {footer && (
          <div className={`flex-shrink-0 px-3 sm:px-6 pt-4 border-t border-[var(--color-semantic-border-primary)] ${fullScreenOnPhone ? "max-sm:pb-[env(safe-area-inset-bottom)]" : ""}`}>
            <div className="flex justify-end gap-3">{footer}</div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GenericModal;
